import * as React from 'react'
import { useEffect, useMemo } from 'react'
import {
    Button,
    Field,
    makeStyles,
    Menu,
    MenuItemRadio,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import {
    AddFilled,
    AddRegular,
    bundleIcon,
    CheckmarkCircleRegular,
    CheckmarkFilled,
    DismissRegular,
    DocumentRibbonRegular,
    NewRegular,
    ShiftsActivityRegular,
} from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import { AppAction } from '../../config/appConfig'
import { MenuItemKey } from '../../components/MainMenu'
import { useTranslation } from 'react-i18next'
import useUser from '../../hooks/useUser'
import PersonAvatar from '../../components/PersonAvatar'
import { Link, useHistory } from 'react-router-dom'
import useViewport, { Viewport } from '../../hooks/useViewport'
import UserPicker from '../../components/TagPicker/UserPicker'
import { AttributeEntityDto } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterType } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import InvoiceApprovalStatusPicker, { InvoiceApprovalStatusLabels } from '../../components/TagPicker/InvoiceApprovalStatusPicker'
import useUsers from '../../hooks/useUsers'
import { FileAction, IOverviewItemDto, IReferenceDto } from '../../config/fileConfig'
import FormattedDate from '../../components/Date/FormattedDate'
import FilePicker from '../../components/TagPicker/FilePicker'
import { InvoiceSummaryDto } from '../../config/finance/invoiceConfig'
import BaseHeaderItem from '../BaseOverview/BaseHeaderItem'
import { UserStatus } from '../../config/userConfig'

const useItemStyles = makeStyles({
    listInvoiceApprovalItem: {
        display: 'grid',
        gap: tokens.spacingVerticalS,
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewIconWidth) auto var(--baseOverviewFileWidth) var(--baseOverviewIconWidth) var(--baseOverviewDateWidth)',
    },
    listInvoiceApprovalItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) var(--baseOverviewIconWidth) 1fr',
        gridTemplateRows: 'auto 1fr',
    },
    listInvoiceApprovalItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewIconWidth) var(--baseOverviewDateWidth)',
    },
})

const AddIcon = bundleIcon(AddFilled, AddRegular)

export type InvoiceApprovalOverviewDto = {
    id: string
    date: Date
    userId: string
    files: IReferenceDto[]
    status: InvoiceApprovalStatus
    currentUserStatus?: InvoiceApprovalStatus
}

export type UserInvoiceApprovalDto = {
    id: string
    userId: string
    status: InvoiceApprovalStatus
    comment: string
    ownerComment: string

    //used in frontend only
    fullName?: string
}

export type InvoiceApprovalDto = {
    id: string
    date: Date
    userId: string
    files: IOverviewItemDto[]
    clientId: string
    debtorId: string
    invoiceEmail: string
    invoiceReference: string
    userApprovals: UserInvoiceApprovalDto[]
    status: InvoiceApprovalStatus
    disbursements: string[]
    fixedPrices: string[]
    times: string[]
    travels: string[]
    invoice?: InvoiceSummaryDto
}

export enum InvoiceApprovalStatus {
    New = 'NEW',
    Active = 'ACTIVE',
    Approved = 'APPROVED',
    OwnerApproved = 'OWNER_APPROVED',
    Done = 'DONE',
    Rejected = 'REJECTED',
}

type InvoiceApprovalFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        users: string[]
        fileOwners: string[]
        files: string[]
        statuses: InvoiceApprovalStatus[]
    }

type InvoiceApprovalSorting = {
    id: string
    date: string
}

type InvoiceApprovalSortingType = BaseOverviewSortingType<InvoiceApprovalSorting>

function getStatusIcon(status?: InvoiceApprovalStatus) {
    switch (status) {
        case InvoiceApprovalStatus.New:
            return <NewRegular />
        case InvoiceApprovalStatus.Active:
            return <ShiftsActivityRegular />
        case InvoiceApprovalStatus.Rejected:
            return <DismissRegular />
        case InvoiceApprovalStatus.Approved:
            return <CheckmarkFilled />
        case InvoiceApprovalStatus.OwnerApproved:
            return <CheckmarkCircleRegular />
        case InvoiceApprovalStatus.Done:
            return <DocumentRibbonRegular />
        default:
            return null
    }
}

export function InvoiceApprovalFilter() {
    const { values, setFieldValue } = useFormikContext<InvoiceApprovalFilterType>()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    return (
        <>
            <Field label={translate('invoiceapproval.filter.user')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.users || []}
                    placeholderKey={'invoiceapproval.filter.user'}
                    onOptionsSelect={(_a, selectedOptions) => setFieldValue('users', selectedOptions)}
                />
            </Field>
            <Field label={translate('invoiceapproval.filter.file.owner')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.fileOwners || []}
                    onOptionsSelect={(_a, selectedOptions) => setFieldValue('fileOwners', selectedOptions)}
                />
            </Field>
            <Field label={translate('invoiceapproval.filter.file')} orientation={orientation}>
                <FilePicker selectedOptions={values.files || []} onOptionsSelect={(_a, selectedOptions) => setFieldValue('files', selectedOptions)} />
            </Field>
            <Field label={translate('invoiceapproval.status.label')} orientation={orientation}>
                <InvoiceApprovalStatusPicker
                    selectedOptions={values.statuses || []}
                    onOptionsSelect={(_a, _b, selectedEntities) => setFieldValue('statuses', selectedEntities)}
                />
            </Field>
        </>
    )
}

const headerSortingItems: InvoiceApprovalSortingType[] = [
    {
        property: ['date', 'id'],
        direction: SortDirection.Ascending,
    },
    {
        property: ['date', 'id'],
        direction: SortDirection.Descending,
        default: true,
    },
]

function HeaderFilterTags(props: BaseOverviewProps<InvoiceApprovalOverviewDto, InvoiceApprovalFilterType, InvoiceApprovalSortingType>) {
    const { values, setValues } = useFormikContext<InvoiceApprovalFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()

    const checkedValues = useMemo<Record<string, string[]>>(
        () => ({
            tag: values.tag ? [`${values.tag}`] : [],
        }),
        [values.tag],
    )

    return (
        <Menu positioning={{ autoSize: true }} checkedValues={checkedValues}>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip withArrow relationship={'label'} content={translate('invoiceapproval.filter.tag.tooltip')}>
                    <Button
                        appearance={'secondary'}
                        size={'small'}
                        className={mergeClasses(overviewStyles.filterTagButton, values.tag && overviewStyles.filterTagButtonSelected)}
                        icon={!values.tag ? <ShiftsActivityRegular /> : undefined}
                    >
                        {values.tag ? translate(`invoiceapproval.filter.tag.${values.tag}`) : undefined}
                    </Button>
                </Tooltip>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    <MenuItemRadio
                        key={0}
                        name={'tag'}
                        value={`myhours`}
                        onClick={() => {
                            setValues(props.defaultFilterValues)
                        }}
                    >
                        {translate('invoiceapproval.filter.tag.myhours')}
                    </MenuItemRadio>
                    <MenuItemRadio
                        key={1}
                        name={'tag'}
                        value={`myfiles`}
                        onClick={() =>
                            setValues({
                                ...props.defaultFilterValues,
                                users: [],
                                fileOwners: [currentUser?.id],
                                tag: 'myfiles',
                            })
                        }
                    >
                        {translate('invoiceapproval.filter.tag.myfiles')}
                    </MenuItemRadio>
                    <MenuItemRadio
                        key={2}
                        name={'tag'}
                        value={`billable`}
                        onClick={() =>
                            setValues({
                                ...props.defaultFilterValues,
                                users: [],
                                statuses: [InvoiceApprovalStatus.OwnerApproved],
                                tag: 'billable',
                            })
                        }
                    >
                        {translate('invoiceapproval.filter.tag.billable')}
                    </MenuItemRadio>
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

export function HeaderActionsButtons() {
    const history = useHistory()
    const { t: translate } = useTranslation()

    return (
        <Tooltip withArrow relationship={'label'} content={translate('action.create.invoiceapproval')}>
            <Button icon={<AddIcon />} appearance={'primary'} onClick={() => history.push('/invoice-approval/add')} />
        </Tooltip>
    )
}

export function InvoiceApprovalListItem(
    props: BaseOverviewLineItemProps<InvoiceApprovalOverviewDto, InvoiceApprovalFilterType, InvoiceApprovalSortingType>,
) {
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()
    const { t: translate } = useTranslation()
    const { getUserById } = useUsers()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    const userStatusIcon = getStatusIcon(props.item.currentUserStatus)
    const totalStatusIcon = getStatusIcon(props.item.status)

    if (listItemSmall) {
        return (
            <>
                <PersonAvatar name={getUserById(props.item.files?.[0].ownerId).fullName} color={'colorful'} size={24} />
                <Tooltip
                    relationship={'label'}
                    content={`${translate('invoiceapproval.status.your')}: ${translate(InvoiceApprovalStatusLabels.get(props.item.currentUserStatus)?.name)}`}
                    withArrow
                >
                    <div className={overviewStyles.centerItems}>{userStatusIcon}</div>
                </Tooltip>
                <Link key={props.item.id} to={`/invoice-approval/${props.item.id}`}>
                    {props.item.files[0].name}
                </Link>
                <div></div>
                <div></div>
                <div className={mergeClasses(itemStyles.listInvoiceApprovalItemSmallBottomRow)}>
                    <Tooltip relationship={'label'} content={props.item.files.map(file => `${file.reference}: ${file.name}`).join(', ')} withArrow>
                        <Link to={`/file/${props.item.files[0]?.id}`}>{props.item.files[0]?.reference}</Link>
                    </Tooltip>
                    <Tooltip
                        relationship={'label'}
                        content={`${translate('invoiceapproval.status.your')}: ${translate(InvoiceApprovalStatusLabels.get(props.item.status)?.name)}`}
                        withArrow
                    >
                        <div className={overviewStyles.centerItems}>{totalStatusIcon}</div>
                    </Tooltip>
                    <div className={overviewStyles.alignEnd}>
                        <FormattedDate date={props.item.date} />
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <PersonAvatar name={getUserById(props.item.files?.[0].ownerId).fullName} color={'colorful'} size={24} />
            <Tooltip
                relationship={'label'}
                content={`${translate('invoiceapproval.status.your')}: ${translate(InvoiceApprovalStatusLabels.get(props.item.currentUserStatus)?.name)}`}
                withArrow
            >
                <div className={overviewStyles.centerItems}>{userStatusIcon}</div>
            </Tooltip>
            <div className={mergeClasses(overviewStyles.flexColumn)}>
                <Link key={props.item.id} to={`/invoice-approval/${props.item.id}`}>
                    {props.item.files[0].name}
                </Link>
            </div>
            <Tooltip relationship={'label'} content={props.item.files.map(file => `${file.reference}: ${file.name}`).join(', ')} withArrow>
                <Link to={`/file/${props.item.files[0]?.id}`}>{props.item.files[0]?.reference}</Link>
            </Tooltip>
            <Tooltip
                relationship={'label'}
                content={`${translate('invoiceapproval.status.total')}: ${translate(InvoiceApprovalStatusLabels.get(props.item.status).name)}`}
                withArrow
            >
                <div className={overviewStyles.centerItems}>{totalStatusIcon}</div>
            </Tooltip>
            <div className={overviewStyles.alignEnd}>
                <FormattedDate date={props.item.date} />
            </div>
        </>
    )
}

export default function InvoiceApprovalOverview() {
    const axios = useAxios()
    const dispatch = useDispatch()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { viewport, isViewport } = useViewport()
    const { currentUser } = useUser()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    //Reset breadcrumbs and data
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.InvoiceApproval] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [])

    const defaultFilterValues = useMemo<InvoiceApprovalFilterType>(
        () => ({
            users: currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            fileOwners: [],
            files: [],
            statuses: [InvoiceApprovalStatus.New, InvoiceApprovalStatus.Active, InvoiceApprovalStatus.Approved],
            query: '',
            tag: 'myhours',
        }),
        [],
    )

    const countItems = (_column: number, filterValues?: InvoiceApprovalFilterType): Promise<BaseOverviewCount> => {
        return axios.get('/finance/invoice-approval/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        _column: number,
        filterValues?: InvoiceApprovalFilterType,
        sortingValues?: InvoiceApprovalSortingType,
        start?: number,
        size?: number,
    ): Promise<InvoiceApprovalOverviewDto[]> => {
        return axios
            .get('/finance/invoice-approval', {
                params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) },
            })
            .then((response: AxiosResponse<InvoiceApprovalOverviewDto[]>) => response?.data || [])
    }

    return (
        <BaseOverview<InvoiceApprovalOverviewDto, InvoiceApprovalFilterType, InvoiceApprovalSortingType>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={BaseHeaderItem}
            listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
            listItemContent={props => <InvoiceApprovalListItem {...props} />}
            listItemClassnames={() => [itemStyles.listInvoiceApprovalItem, listItemSmall && itemStyles.listInvoiceApprovalItemSmall]}
            headerActionsButtons={HeaderActionsButtons}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            filterContent={InvoiceApprovalFilter}
            inputPlaceholderKey={'file.filter.search'}
            localStorageKeyPrefix={'HammockInvoiceApprovalOverview'}
        />
    )
}
